import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowRightIcon,
  BuildingOffice2Icon,
  MapPinIcon,
  MagnifyingGlassIcon,
  TrophyIcon,
  UserCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Navbar from "../../components/navBar";
import LikeButton from "../../components/LikeButton";

const API_URL = process.env.REACT_APP_API_URL;

function getList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export default function ClubsPage() {
  const [clubs, setClubs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [leagues, setLeagues] = useState([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState("all");
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024
  );
  const [isCollapsed, setIsCollapsed] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem("desac-sidebar-collapsed") === "true"
  );
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [clubsResponse, leaguesResponse, coachesResponse] = await Promise.all([
        axios.get(`${API_URL}/clubs`),
        axios.get(`${API_URL}/leagues`),
        axios.get(`${API_URL}/coaches`),
      ]);
      setClubs(getList(clubsResponse.data));
      setLeagues(getList(leaguesResponse.data));
      setCoaches(getList(coachesResponse.data));

      const userId = localStorage.getItem("userId");
      if (userId) setCurrentUser({ userId: Number.parseInt(userId, 10) });
    } catch (fetchError) {
      console.error("Error fetching clubs:", fetchError);
      setError("We couldn't load the club directory. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const updateViewport = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsCollapsed(window.localStorage.getItem("desac-sidebar-collapsed") === "true");
    };
    const updateCollapsedState = (event) => {
      setIsCollapsed(
        typeof event?.detail?.collapsed === "boolean"
          ? event.detail.collapsed
          : window.localStorage.getItem("desac-sidebar-collapsed") === "true"
      );
    };
    window.addEventListener("resize", updateViewport);
    window.addEventListener("storage", updateCollapsedState);
    window.addEventListener("desac-sidebar-toggle", updateCollapsedState);
    return () => {
      window.removeEventListener("resize", updateViewport);
      window.removeEventListener("storage", updateCollapsedState);
      window.removeEventListener("desac-sidebar-toggle", updateCollapsedState);
    };
  }, []);

  const locations = useMemo(() => {
    const uniqueLocations = [...new Set(clubs.map((club) => club.location).filter(Boolean))];
    return uniqueLocations.sort((a, b) => a.localeCompare(b));
  }, [clubs]);

  const filteredClubs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const activeLeague = leagues.find((league) => String(league.leagueId) === selectedLeagueId);
    return clubs.filter((club) => {
      const searchable = [club.name, club.location, club.description, club.foundedYear]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      const matchesSearch = !query || searchable.includes(query);
      const matchesLocation = selectedLocation === "all" || club.location === selectedLocation;
      const matchesLeague = !activeLeague || (activeLeague.clubs || []).some(
        (leagueClub) => leagueClub.clubId === club.clubId
      );
      return matchesSearch && matchesLocation && matchesLeague;
    });
  }, [clubs, leagues, searchQuery, selectedLocation, selectedLeagueId]);

  const sidebarWidth = isDesktop && isCollapsed ? "5rem" : "16rem";
  const hasFilters = Boolean(searchQuery) || selectedLocation !== "all" || selectedLeagueId !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedLocation("all");
    setSelectedLeagueId("all");
  };

  const handleCoachClick = (event, coachId) => {
    event.stopPropagation();
    navigate(`/sports/coach/${coachId}`);
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <main
          style={{ "--clubs-sidebar-width": sidebarWidth }}
          className="min-h-screen bg-surface-950 px-5 pb-10 pt-24 text-white md:ml-64 md:pt-20 lg:ml-[var(--clubs-sidebar-width)] sm:px-8"
        >
          <div role="status" aria-label="Loading clubs" className="mx-auto flex min-h-72 max-w-7xl items-center justify-center gap-3 rounded-xl border border-line bg-surface-900 text-sm text-gray-300">
            <span aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-surface-600 border-t-accent" />
            Loading clubs…
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main
        style={{ "--clubs-sidebar-width": sidebarWidth }}
        className="min-h-screen overflow-x-hidden bg-surface-950 pb-12 pt-16 text-white md:ml-64 md:pt-0 lg:ml-[var(--clubs-sidebar-width)]"
      >
        <header className="border-b border-line bg-surface-900">
          <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-8 sm:px-8 sm:py-10 lg:flex-row lg:items-end lg:justify-between lg:px-10">
            <div className="max-w-2xl">
              <p className="eyebrow-accent inline-flex items-center gap-2">
                <BuildingOffice2Icon aria-hidden="true" className="h-4 w-4" />
                CLUB DIRECTORY
              </p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">Sports clubs</h1>
              <p className="mt-2 text-sm leading-6 text-gray-400 sm:text-base">
                Discover clubs, explore their profiles, and meet the people behind the teams.
              </p>
            </div>
            <div aria-live="polite" className="inline-flex min-h-10 w-fit items-center gap-2 rounded-lg border border-line bg-surface-850 px-3 text-sm text-gray-300">
              <BuildingOffice2Icon aria-hidden="true" className="h-4 w-4 text-accent-soft" />
              {filteredClubs.length} {filteredClubs.length === 1 ? "club" : "clubs"}
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8 sm:py-8 lg:px-10">
          <section aria-label="Search and filter clubs" className="rounded-xl border border-line bg-surface-900 p-4 sm:p-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(180px,240px)]">
              <label className="relative block min-w-0">
                <span className="sr-only">Search clubs</span>
                <MagnifyingGlassIcon aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search club names or details"
                  className="field min-h-11 pl-10 pr-10 text-sm"
                />
                {searchQuery && (
                  <button type="button" aria-label="Clear search" onClick={() => setSearchQuery("")} className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-surface-700 hover:text-white">
                    <XMarkIcon aria-hidden="true" className="h-4 w-4" />
                  </button>
                )}
              </label>
              <label>
                <span className="sr-only">Filter clubs by location</span>
                <select value={selectedLocation} onChange={(event) => setSelectedLocation(event.target.value)} className="field min-h-11 text-sm">
                  <option value="all">All locations</option>
                  {locations.map((location) => <option key={location} value={location}>{location}</option>)}
                </select>
              </label>
            </div>

            <div className="mt-4 border-t border-line pt-4">
              <div className="mb-2 flex items-center gap-2">
                <TrophyIcon aria-hidden="true" className="h-4 w-4 text-accent-soft" />
                <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">Competitions</h2>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1" aria-label="Filter clubs by competition">
                <button
                  type="button"
                  aria-pressed={selectedLeagueId === "all"}
                  onClick={() => setSelectedLeagueId("all")}
                  className={`min-h-9 shrink-0 rounded-lg border px-3 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${selectedLeagueId === "all" ? "border-accent/30 bg-accent/10 text-accent-soft" : "border-line bg-surface-850 text-gray-400 hover:bg-surface-800 hover:text-white"}`}
                >
                  All competitions
                </button>
                {leagues.map((league) => (
                  <button
                    key={league.leagueId}
                    type="button"
                    aria-pressed={selectedLeagueId === String(league.leagueId)}
                    onClick={() => setSelectedLeagueId(String(league.leagueId))}
                    className={`min-h-9 shrink-0 rounded-lg border px-3 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${selectedLeagueId === String(league.leagueId) ? "border-accent/30 bg-accent/10 text-accent-soft" : "border-line bg-surface-850 text-gray-400 hover:bg-surface-800 hover:text-white"}`}
                  >
                    {league.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
              <p>Showing {filteredClubs.length} of {clubs.length} {clubs.length === 1 ? "club" : "clubs"}</p>
              {hasFilters && <button type="button" onClick={clearFilters} className="font-medium text-gray-300 underline decoration-line-strong underline-offset-4 transition-colors hover:text-white">Clear filters</button>}
            </div>
          </section>

          {error ? (
            <section role="alert" className="mt-6 rounded-xl border border-line bg-surface-900 px-5 py-12 text-center">
              <BuildingOffice2Icon aria-hidden="true" className="mx-auto h-9 w-9 text-gray-500" />
              <h2 className="mt-4 text-lg font-semibold text-white">Clubs are temporarily unavailable</h2>
              <p className="mt-2 text-sm text-gray-400">{error}</p>
              <button type="button" onClick={fetchData} className="btn-primary mt-5 min-h-11">Try again</button>
            </section>
          ) : filteredClubs.length === 0 ? (
            <section className="mt-6 rounded-xl border border-line bg-surface-900 px-5 py-12 text-center">
              <MagnifyingGlassIcon aria-hidden="true" className="mx-auto h-9 w-9 text-gray-500" />
              <h2 className="mt-4 text-lg font-semibold text-white">{clubs.length ? "No clubs match these filters" : "No clubs available"}</h2>
              <p className="mt-2 text-sm text-gray-400">{clubs.length ? "Try another club name, location, or competition." : "Clubs will appear here when they are available."}</p>
              {hasFilters && <button type="button" onClick={clearFilters} className="btn-secondary mt-5 min-h-11">Clear filters</button>}
            </section>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {filteredClubs.map((club) => {
                const clubCoach = coaches.find((coach) => coach.clubId === club.clubId);
                return (
                  <ClubCard
                    key={club.clubId}
                    club={club}
                    coach={clubCoach}
                    currentUser={currentUser}
                    onOpen={() => navigate(`/sports/club/${club.clubId}`)}
                    onCoachClick={handleCoachClick}
                  />
                );
              })}
            </div>
          )}
        </div>
      </main>
    </>
  );
}

function ClubCard({ club, coach, currentUser, onOpen, onCoachClick }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const initials = club.name?.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "?";

  return (
    <article className="group flex min-w-0 flex-col overflow-hidden rounded-xl border border-line bg-surface-850 transition-all duration-200 hover:-translate-y-0.5 hover:border-line-strong hover:bg-surface-800">
      <button type="button" onClick={onOpen} aria-label={`View ${club.name || "club"} profile`} className="flex min-w-0 flex-1 flex-col text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-accent">
        <div className="flex items-center gap-4 border-b border-line bg-surface-900 p-4 sm:p-5">
          <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-line bg-surface-800 sm:h-[72px] sm:w-[72px]">
            {club.logo && !logoFailed ? (
              <img src={club.logo} alt={`${club.name || "Club"} logo`} loading="lazy" onError={() => setLogoFailed(true)} className="h-full w-full object-contain p-2" />
            ) : (
              <span aria-hidden="true" className="text-lg font-bold tracking-wide text-accent-soft">{initials}</span>
            )}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-lg font-semibold tracking-tight text-white transition-colors group-hover:text-accent-soft">{club.name || "Club name unavailable"}</span>
            <span className="mt-1 inline-flex max-w-full items-center gap-1.5 text-xs text-gray-400">
              <MapPinIcon aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-accent-soft" />
              <span className="truncate">{club.location || "Location unavailable"}</span>
            </span>
          </span>
          {currentUser && (
            <span className="shrink-0" onClick={(event) => event.stopPropagation()}>
              <LikeButton entityId={club.clubId} entityType="Club" userId={currentUser.userId} size="small" />
            </span>
          )}
        </div>
        <span className="flex min-w-0 flex-1 flex-col p-4 sm:p-5">
          <span className="grid grid-cols-2 gap-3">
            <span className="min-w-0 rounded-lg border border-line bg-surface-900 px-3 py-2.5">
              <span className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500">Founded</span>
              <span className="mt-1 block truncate text-sm font-medium text-gray-200">{club.foundedYear || "Not listed"}</span>
            </span>
            <span className="min-w-0 rounded-lg border border-line bg-surface-900 px-3 py-2.5">
              <span className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500">Club profile</span>
              <span className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-gray-200">View details <ArrowRightIcon aria-hidden="true" className="h-3.5 w-3.5 text-accent-soft transition-transform group-hover:translate-x-0.5" /></span>
            </span>
          </span>
          <span className="mt-4 line-clamp-3 min-h-[4.5rem] text-sm leading-6 text-gray-400">
            {club.description || "Explore this club's profile for more information."}
          </span>
        </span>
      </button>

      <div className="border-t border-line px-4 py-3 sm:px-5">
        {coach ? (
          <div className="flex min-w-0 items-center gap-2 text-sm">
            <UserCircleIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-500" />
            <span className="shrink-0 text-xs text-gray-500">Coach</span>
            <button type="button" onClick={(event) => onCoachClick(event, coach.id)} className="min-w-0 truncate text-left text-sm font-medium text-gray-300 underline decoration-line-strong underline-offset-4 transition-colors hover:text-accent-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent">
              {coach.firstName} {coach.lastName}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <UserCircleIcon aria-hidden="true" className="h-4 w-4" />
            No coach assigned
          </div>
        )}
      </div>
    </article>
  );
}
